import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { apiClient } from '../axios.js';
import { type ErrorResponseData, extractMessageFromResponseData } from '../lib/error.js';
import { getStopErrorCode, isBenignStopError } from '../services/suppressions.js';
import {
  type BroadbandSuppression,
  broadbandSuppressionListSchema,
  broadbandSuppressionSchema,
} from '../types/broadband-suppression.js';
import type { SignalType } from '../types/signal-type.js';

export type UseGetBroadbandSuppressionsParams = {
  deviceId: string;
  page: number;
  pageSize: number;
};

type BroadbandSuppressionRequestBase = {
  type: SignalType;
  duration: number;
};

export type CreateBroadbandSuppressionRequest =
  | (BroadbandSuppressionRequestBase & {
      mode: 'solid';
      channelFrequencyStart: number;
      channelFrequencyStop: number;
    })
  | (BroadbandSuppressionRequestBase & {
      mode: 'hopping';
      channelFrequencyStart: number;
      channelFrequencyStop: number;
      bandwidth: number;
      step: number;
      stepStandingTimeMs: number;
    })
  | (BroadbandSuppressionRequestBase & {
      mode: 'multi';
      channelFrequencyList: number[];
      bandwidth: number;
      stepStandingTimeMs: number;
    });

export type CreateBroadbandSuppressionPayload = {
  deviceId: string;
  payload: CreateBroadbandSuppressionRequest;
};

export type StopBroadbandSuppressionPayload = {
  deviceId: string;
  suppressionId: string;
};

export function useGetBroadbandSuppressions({
  deviceId,
  page,
  pageSize,
}: UseGetBroadbandSuppressionsParams) {
  return useQuery({
    queryKey: ['broadband-suppressions', { deviceId, page, pageSize }],
    queryFn: async () => {
      const response = await apiClient.get(`/devices/${deviceId}/broadband_suppressions`, {
        params: { page, pageSize },
      });
      return broadbandSuppressionListSchema.parse(response.data);
    },
    enabled: Boolean(deviceId),
  });
}

// No rejection on this route carries a business code (unlike the stop route), so unlike
// `StopBroadbandSuppressionError` this carries `serverMessage` instead -- mirrors
// `CreateSuppressionError` in `useSuppressions.ts`.
export type CreateBroadbandSuppressionError = Error & { status?: number; serverMessage?: string };

const isClientRejection = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || !error.response) {
    return false;
  }
  const { status } = error.response;
  return status >= 400 && status < 500;
};

export function useCreateBroadbandSuppression() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deviceId,
      payload,
    }: CreateBroadbandSuppressionPayload): Promise<BroadbandSuppression> => {
      try {
        const { data } = await apiClient.post(
          `/devices/${deviceId}/broadband_suppressions`,
          payload,
        );
        return broadbandSuppressionSchema.parse(data);
      } catch (error) {
        // A rejected create is an expected outcome on the repeat path (the device's bounds may
        // have moved since the original ran), and the shared axios interceptor already reports it
        // — capturing here too would file two events per refusal.
        if (!isClientRejection(error)) {
          Sentry.captureException(error);
        }
        console.debug({ error });
        const thrown: CreateBroadbandSuppressionError = new Error(
          'Failed to create broadband suppression.',
        );
        thrown.status = axios.isAxiosError(error) ? error.response?.status : undefined;
        // Read straight off the body, mirroring `useCreateSuppression` -- `extractErrorMessage`'s
        // `error.message` fallback would surface axios's untranslated "Request failed with status
        // code 400" — or a literal "Network Error" — in place of the caller's localized frame. The
        // `error.response` guard also covers this `try`'s other throw site, the 2xx schema parse,
        // which carries no response at all.
        thrown.serverMessage =
          axios.isAxiosError(error) && error.response
            ? (extractMessageFromResponseData(error.response.data as ErrorResponseData) ??
              undefined)
            : undefined;
        throw thrown;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['broadband-suppressions'] });
      queryClient.invalidateQueries({
        queryKey: ['broadband-suppressions', { deviceId: variables.deviceId }],
      });
    },
  });
}

export type StopBroadbandSuppressionError = Error & { status?: number; code?: string };

// Shared with the Sunflower broadband dialog: changes here must be additive. `status`/`code` are
// attached to the thrown error without changing its message, but the benign-error Sentry skip
// below applies to Sunflower's stop clicks too, not just the multi-frequency path.
export function useStopBroadbandSuppression() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, suppressionId }: StopBroadbandSuppressionPayload) => {
      try {
        await apiClient.post(`/devices/${deviceId}/broadband_suppressions/${suppressionId}/stop`);
      } catch (error) {
        // Benign rejection (row already finished / gone) is an expected outcome, not a bug.
        if (!isBenignStopError(error)) {
          Sentry.captureException(error);
        }
        console.debug({ error });
        const thrown: StopBroadbandSuppressionError = new Error(
          'Failed to stop broadband suppression.',
        );
        thrown.status = axios.isAxiosError(error) ? error.response?.status : undefined;
        thrown.code = getStopErrorCode(error);
        throw thrown;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['broadband-suppressions'] });
      queryClient.invalidateQueries({
        queryKey: ['broadband-suppressions', { deviceId: variables.deviceId }],
      });
      queryClient.invalidateQueries({ queryKey: ['all-suppressions'] });
    },
  });
}
